import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Download, ExternalLink, FileText, Loader2, RefreshCw } from 'lucide-react'

/**
 * Full-screen, in-app PDF viewer overlay.
 *
 * Loads remote PDFs as same-origin `blob:` object URLs so Chromium/Edge
 * can embed them without cross-origin PDF plugin CORS failures. Download
 * and Open-in-new-tab still use the original `fileUrl`.
 *
 * Parent-owned `blob:` URLs (e.g. import source PDFs) are used as-is —
 * never re-fetched or cloned, to avoid revoke/empty-blob races.
 */
export interface PdfViewerProps {
  /** Presigned S3 URL, blob URL, or any other URL pointing at a PDF. */
  fileUrl: string
  /**
   * Optional display name. The `.pdf` extension (case-insensitive) is
   * stripped from the header label. When absent, falls back to the last
   * path segment of `fileUrl`.
   */
  fileName?: string
  /**
   * Optional file size in kilobytes; rendered under the file name as
   * `PDF · <n.n> KB`. Omitted entirely when undefined / NaN.
   */
  fileSizeKB?: number
  /**
   * Invoked when the user clicks Close or presses Escape. The overlay
   * does not manage its own visibility — parents must conditionally
   * render the component.
   */
  onClose: () => void
}

function isPdfMagic(buffer: ArrayBuffer): boolean {
  const head = new Uint8Array(buffer.slice(0, 4))
  return (
    head.length >= 4 &&
    head[0] === 0x25 && // %
    head[1] === 0x50 && // P
    head[2] === 0x44 && // D
    head[3] === 0x46 // F
  )
}

/**
 * For remote URLs: fetch bytes, require %PDF magic, return a same-origin
 * object URL the iframe can embed. Caller owns revoke.
 *
 * Parent-owned `blob:` URLs are returned as-is — never re-fetched or cloned
 * (cloning + revoke races empty Chromium's PDF plugin).
 */
async function toPdfObjectUrl(sourceUrl: string): Promise<{
  url: string
  owned: boolean
}> {
  if (sourceUrl.startsWith('blob:')) {
    return { url: sourceUrl, owned: false }
  }
  const response = await fetch(sourceUrl)
  if (!response.ok) {
    throw new Error(`Could not load PDF (${response.status})`)
  }
  const buffer = await response.arrayBuffer()
  // Reject obvious non-PDF bodies (S3 XML error pages, HTML, etc.)
  if (!isPdfMagic(buffer)) {
    throw new Error('Response is not a PDF')
  }
  return {
    url: URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' })),
    owned: true
  }
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  fileUrl,
  fileName,
  fileSizeKB,
  onClose
}) => {
  const [iframeSrc, setIframeSrc] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    let cancelled = false
    let ownedObjectUrl: string | null = null

    setLoading(true)
    setLoadError(null)
    setIframeSrc(null)

    void (async () => {
      try {
        const resolved = await toPdfObjectUrl(fileUrl)
        if (cancelled) {
          if (resolved.owned) URL.revokeObjectURL(resolved.url)
          return
        }
        if (resolved.owned) ownedObjectUrl = resolved.url
        setIframeSrc(resolved.url)
        setLoading(false)
      } catch (err) {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : 'Could not open this PDF')
        setLoading(false)
      }
    })()

    return () => {
      cancelled = true
      // Only revoke URLs this component created — never parent-owned blob: URLs.
      if (ownedObjectUrl) URL.revokeObjectURL(ownedObjectUrl)
    }
  }, [fileUrl, reloadToken])

  const handleDownload = async () => {
    if (!fileUrl) return
    try {
      const response = await fetch(fileUrl)
      const buffer = await response.arrayBuffer()
      if (!isPdfMagic(buffer)) {
        throw new Error('Response is not a PDF')
      }
      const blob = new Blob([buffer], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || fileUrl.split('/').pop() || 'document.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      // Delay revoke so Chromium can finish reading the blob for download.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    } catch (error) {
      console.error('Error downloading PDF:', error)
      window.open(fileUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const handleOpenInNewTab = () => {
    window.open(fileUrl, '_blank', 'noopener,noreferrer')
  }

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [onClose])

  const sizeLabel =
    typeof fileSizeKB === 'number' && !Number.isNaN(fileSizeKB)
      ? `${fileSizeKB.toFixed(1)} KB`
      : undefined

  const displayName = (fileName || fileUrl.split('/').pop() || 'Document').replace(
    /\.pdf$/i,
    ''
  )

  return createPortal(
    <div
      data-testid="pdf-viewer"
      role="dialog"
      aria-label={displayName}
      className="bg-page-bg flex flex-col"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        zIndex: 2147483647
      }}
    >
      <div className="bg-blueprint-blue h-[60px] px-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
            <FileText className="h-4 w-4 text-white" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[13px] font-medium text-white truncate max-w-[240px] sm:max-w-[480px]">
              {displayName}
            </span>
            {sizeLabel && (
              <span className="text-[11px] text-white/50 mt-0.5">PDF · {sizeLabel}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="flex items-center gap-1.5 px-3 h-8 text-[12px] font-medium text-white border border-white/20 rounded-md hover:bg-white/10 transition-colors"
            title="Open in New Tab"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Open</span>
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 h-8 text-[12px] font-medium bg-hero-red text-white rounded-md hover:bg-hero-red-60 transition-colors"
            title="Download PDF"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Download</span>
          </button>
          <div className="w-px h-5 bg-white/15 mx-1" />
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-md transition-colors"
            title="Close (Esc)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden bg-page-bg min-h-0 relative">
        {loading && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-page-bg"
            data-testid="pdf-viewer-loading"
          >
            <Loader2 className="h-8 w-8 animate-spin text-hero-red" />
            <p className="text-[13px] text-heritage-grey">Loading PDF…</p>
          </div>
        )}
        {loadError && !loading && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-page-bg px-6"
            data-testid="pdf-viewer-error"
          >
            <p className="font-heading text-[16px] font-semibold text-blueprint-blue">
              We can&apos;t open this file
            </p>
            <p className="text-[13px] text-heritage-grey text-center max-w-sm">{loadError}</p>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={() => setReloadToken((n) => n + 1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blueprint-blue text-white text-[12px] font-medium hover:opacity-90"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Retry
              </button>
              <button
                type="button"
                onClick={handleOpenInNewTab}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#E2E5EA] text-text-grey text-[12px] font-medium hover:border-heritage-grey"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in new tab
              </button>
            </div>
          </div>
        )}
        {iframeSrc && !loadError && (
          <iframe
            src={iframeSrc}
            className="border-0 w-full h-full block"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              margin: 0,
              padding: 0,
              border: 'none'
            }}
            title={fileName || 'PDF Viewer'}
            allow="fullscreen"
            data-testid="pdf-viewer-iframe"
          />
        )}
      </div>
    </div>,
    document.body
  )
}

export default PdfViewer
